import React, { useEffect, useRef, useState } from 'react';
import EtusivuSection from './about/EtusivuSection';
import ManifestiSection from './about/ManifestiSection';
import ChatSection from './about/ChatSection';
import TapahtumatSection from './about/TapahtumatSection';
import GamesSection from './about/GamesSection';
import QASection from './about/QASection';
import TilastotSection from './about/TilastotSection';
import ProfiiliSection from './about/ProfiiliSection';
import LexPage from './about/LexPage';

const SECTIONS = [
  { id: 'etusivu', label: 'Etusivu' },
  { id: 'manifesti', label: 'Manifesti' },
  { id: 'chat', label: 'Chat' },
  { id: 'tapahtumat', label: 'Tapahtumat' },
  { id: 'games', label: 'Pelit' },
  { id: 'saannot', label: 'Säännöt' },
  { id: 'qa', label: 'Q&A' },
  { id: 'tilastot', label: 'Tilastot' },
  { id: 'profiili', label: 'Profiili' },
];

function readSectionFromHash() {
  if (typeof window === 'undefined') return null;
  const id = (window.location.hash || '').replace(/^#/, '').trim();
  if (!id) return null;
  return SECTIONS.some((s) => s.id === id) ? id : null;
}

function scrollAboutToTop() {
  if (typeof window === 'undefined') return;
  /* Top of .App-block (not the h1). Scroll the window so the menu scrolls away — scrollIntoView
     on inner content can scroll only a flex child and leave the header visible. */
  const el = document.getElementById('about-block-top');
  if (el) {
    const y = el.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  if (window.location.hash) {
    const path = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState(null, '', path);
  }
}

function AboutPulinaPage() {
  const [activeSection, setActiveSection] = useState(
    () => readSectionFromHash() || 'etusivu'
  );
  const initialScrollDoneRef = useRef(false);

  useEffect(() => {
    const onHash = () => {
      const hid = readSectionFromHash();
      setActiveSection(hid || 'etusivu');
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    const fromHash = readSectionFromHash();
    if (!initialScrollDoneRef.current) {
      initialScrollDoneRef.current = true;
      if (!fromHash) return;
    }
    /* Etusivu lives under the “Pulina?” title + TOC — scrolling to #etusivu hides the heading
       (e.g. after opening /pulinasta or /pulinasta#etusivu from the Pulina? menu link). */
    if (activeSection === 'etusivu') return;
    requestAnimationFrame(() => {
      document.getElementById(activeSection)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [activeSection]);

  const selectSection = (id) => {
    setActiveSection(id);
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}#${id}`
    );
  };

  const activeLabel = SECTIONS.find((s) => s.id === activeSection)?.label ?? 'Etusivu';

  return (
    <div id="about-block-top" className="App-block AboutPulinaPage">
      <div className="OhjeetPage-backTopWrap">
        <button
          type="button"
          className="Button OhjeetPage-backTop"
          onClick={scrollAboutToTop}
          aria-label="Takaisin sivun alkuun"
        >
          Takaisin ylös
        </button>
      </div>

      <div className="OhjeetPage-container">
        <h1 id="about-top" className="OhjeetPage-title" tabIndex={-1}>
          Pulina?
        </h1>

        <nav className="OhjeetPage-toc" aria-label="Sisällysluettelo">
          {SECTIONS.map(({ id, label }) => (
            <a
              key={id}
              href={`#${id}`}
              aria-current={activeSection === id ? 'page' : undefined}
              onClick={(e) => {
                e.preventDefault();
                selectSection(id);
              }}
            >
              {label}
            </a>
          ))}
        </nav>

        <section
          className="OhjeetPage-section"
          id={activeSection}
          aria-label={activeLabel}
        >
          {activeSection === 'etusivu' && <EtusivuSection />}
          {activeSection === 'manifesti' && <ManifestiSection />}
          {activeSection === 'chat' && <ChatSection />}
          {activeSection === 'tapahtumat' && <TapahtumatSection />}
          {activeSection === 'games' && <GamesSection />}
          {activeSection === 'saannot' && <LexPage />}
          {activeSection === 'qa' && <QASection />}
          {activeSection === 'tilastot' && <TilastotSection />}
          {activeSection === 'profiili' && <ProfiiliSection />}
        </section>
      </div>
    </div>
  );
}

export default AboutPulinaPage;
